'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import PageHero from '@/components/ui/PageHero';

const offerings = [
  { icon: 'DevicePhoneMobileIcon', label: 'Rider App', sub: 'iOS & Android' },
  { icon: 'TruckIcon', label: 'Driver App', sub: 'iOS & Android' },
  { icon: 'ChatBubbleLeftRightIcon', label: 'WhatsApp Booking', sub: 'Chatbot powered' },
  { icon: 'BriefcaseIcon', label: 'Corporate Rental', sub: 'Business travel' },
  { icon: 'ComputerDesktopIcon', label: 'Kiosk Booking', sub: 'Self-service terminal' },
  { icon: 'GlobeAltIcon', label: 'Website / Manual Booking', sub: 'Web platform' },
  { icon: 'UserIcon', label: 'Driver Own Booking', sub: 'Direct bookings' },
  { icon: 'WindowIcon', label: 'Responsive Webpages', sub: 'Mobile-ready' },
  { icon: 'Squares2X2Icon', label: 'Dispatcher Panel', sub: 'Operations control' },
];

const bookingChannels = [
  { icon: 'DevicePhoneMobileIcon', label: 'Mobile App Booking', desc: 'Customer books through mobile app' },
  { icon: 'ChatBubbleLeftRightIcon', label: 'WhatsApp Booking', desc: 'Customer books via WhatsApp' },
  { icon: 'ComputerDesktopIcon', label: 'Kiosk Booking', desc: 'Customer books via Kiosk' },
  { icon: 'GlobeAltIcon', label: 'Website Booking', desc: 'Customer books through website' },
  { icon: 'BriefcaseIcon', label: 'Corporate Rental', desc: 'Business travel managed with monthly billing & reports' },
  { icon: 'UserIcon', label: 'Direct with Driver Booking', desc: 'Customer books directly with driver' },
  { icon: 'PhoneIcon', label: 'Phone Booking (Call)', desc: 'Customer calls → Customer Care → Server' },
];

const vehicleAssignments = [
  { icon: 'TruckIcon', label: 'Auto Ride', color: '#F59E0B' },
  { icon: 'TruckIcon', label: 'Bike Ride', color: '#0459F7' },
  { icon: 'TruckIcon', label: 'Car Ride', color: '#0A2FD8' },
];

const marketReality = [
  'Many traditional operators failed due to slow technology adoption, while a few still manage high volumes through strong driver networks.',
  'Technology empowers local operators to compete with major platforms.',
  "India's informal taxi sector is larger than Ola and Uber combined.",
  'Most operators still rely on phone calls and WhatsApp.',
  'The informal sector contributes nearly 67% of India\'s taxi business.',
];

const movioAdvantage = [
  'Competitive and reliable taxi software solutions are limited in the market.',
  'Most available platforms are expensive and lack real-world validation.',
  'Movio Taxi delivers a proven, high-quality SaaS-based taxi platform.',
  'We help businesses upgrade their operations with modern technology.',
  'Usage-based pricing with no hidden add-on costs.',
];

const fleetBenefits = [
  'Smooth Operations',
  'Excellent Driver Management',
  'Delighted Customers',
  'Cost Control',
  'Competitive Pricing Management',
  'Focused on Business Growth',
  'Customized for Your Region / Language',
  'Customized Financial Model Suitable for Your Business',
];

const fleetFeatures = [
  { icon: 'CalendarDaysIcon', label: 'Booking Management', sub: 'Automated & Manual' },
  { icon: 'MapPinIcon', label: 'Trip Management', sub: 'Manage All Trips' },
  { icon: 'UserGroupIcon', label: 'Driver Management', sub: 'Onboarding, Offboarding & Attendance' },
  { icon: 'MapIcon', label: 'Geo-Fencing', sub: 'Serviceable Location Control' },
  { icon: 'BellAlertIcon', label: 'Driver Prioritization', sub: 'Parallel & Serial Alert' },
  { icon: 'HandRaisedIcon', label: 'Driver Holding', sub: 'Fee Pending Management' },
  { icon: 'CurrencyRupeeIcon', label: 'Fare Management', sub: 'Dynamic fare control' },
  { icon: 'DocumentTextIcon', label: 'Manage Pending Fees', sub: 'Fee tracking' },
  { icon: 'TagIcon', label: 'Location-Based Fares', sub: 'Packages & zone pricing' },
  { icon: 'ShieldCheckIcon', label: 'Secured Fleet Management', sub: 'User access control' },
  { icon: 'CreditCardIcon', label: 'Payment Gateway', sub: 'Multiple integrations' },
  { icon: 'ChartBarIcon', label: 'Reports', sub: 'Daily / Weekly / Monthly' },
];

const driverAppFeatures = [
  'Driver Online / Offline',
  'Trip Alert – Accept / Reject Trip',
  'GPS Based Pickup / Drop Routing',
  'OTP-Based Trip Start / End',
  'Fare Calculation / Acceptance',
  'Take Break Option',
  'Earnings / Ratings',
  'Driver Payment System',
  'Trip History',
  'Call Masking / SMS to the Customer',
  'Connected Customer Care',
  'Multi-Language',
  'Driver Own Booking',
];

const riderAppFeatures = [
  'Trip Booking',
  'Trip History',
  'Fare Enquiry',
  'Book for Others',
  'Cancellation',
  'Auto Booking',
  'Multi-Language',
  'Dark Mode',
  'Referral',
];

const kioskSteps = [
  { icon: 'MapPinIcon', label: 'Select Pickup & Drop' },
  { icon: 'TruckIcon', label: 'Choose Ride Type' },
  { icon: 'CreditCardIcon', label: 'Make Payment' },
  { icon: 'CheckCircleIcon', label: 'Get Confirmed & Start Your Ride' },
];

const kioskFeatures = [
  { icon: 'DevicePhoneMobileIcon', label: 'Touch Screen Interface' },
  { icon: 'CalendarDaysIcon', label: 'Booking Page' },
  { icon: 'MapPinIcon', label: 'Tracking' },
  { icon: 'XCircleIcon', label: 'Booking Cancellation' },
];

const kioskBenefits = [
  { icon: 'BoltIcon', label: 'Fast & Easy', desc: 'Book your taxi in just a few simple steps.' },
  { icon: 'ShieldCheckIcon', label: 'Secure & Reliable', desc: 'Safe transactions and trusted service.' },
  { icon: 'ClockIcon', label: 'Available 24/7', desc: 'Book anytime, anywhere with our kiosk.' },
  { icon: 'CurrencyRupeeIcon', label: 'Multiple Payment Options', desc: 'Pay the way you prefer.' },
];

const whatsappSteps = [
  { step: '1', label: 'Start Chat on WhatsApp' },
  { step: '2', label: 'Share Trip Details (Location, Time, Car Type)' },
  { step: '3', label: 'Get Confirmation & Driver Details' },
  { step: '4', label: 'Enjoy Your Ride' },
];

const whatsappBenefits = [
  { icon: 'CheckCircleIcon', label: 'Easy & Convenient' },
  { icon: 'BoltIcon', label: 'Instant Response' },
  { icon: 'UserGroupIcon', label: 'Widespread User Reach' },
  { icon: 'ClockIcon', label: 'Saves Time' },
  { icon: 'CurrencyRupeeIcon', label: 'Cost Effective' },
];

const corporateFeatures = [
  { icon: 'UserGroupIcon', label: 'Unlimited Corporate Registrations', desc: 'Add and manage unlimited employees under one account.' },
  { icon: 'AdjustmentsHorizontalIcon', label: 'Customizable Access', desc: 'Set roles and permissions for better control.' },
  { icon: 'DocumentChartBarIcon', label: 'Individualized Billing & Reports', desc: 'Get detailed reports and transparent billing for every trip.' },
  { icon: 'ReceiptPercentIcon', label: 'Invoice & Incentives', desc: 'Automated invoices and exciting incentives for your business.' },
  { icon: 'TagIcon', label: 'Corporate Commission & Discounts', desc: 'Enjoy exclusive discounts and lower travel costs.' },
  { icon: 'MapPinIcon', label: 'Live Tracking', desc: 'Track rides in real-time and stay updated on every trip.' },
  { icon: 'DevicePhoneMobileIcon', label: 'Tracks Trips on App', desc: 'Monitor all trips, expenses and reports on the go.' },
];

const driverBookingBenefits = [
  { icon: 'UserGroupIcon', label: 'No need to share trips in common WhatsApp groups' },
  { icon: 'ShieldCheckIcon', label: 'Protect driver–customer relationships' },
  { icon: 'ArrowPathIcon', label: 'Easily reassign trips to verified drivers' },
  { icon: 'DocumentTextIcon', label: 'All trips securely recorded on the platform' },
  { icon: 'LockClosedIcon', label: 'Customer phone numbers remain protected' },
  { icon: 'MapPinIcon', label: 'Live trip tracking with safety support' },
  { icon: 'CurrencyRupeeIcon', label: 'More opportunities for trips, incentives, and commissions' },
  { icon: 'BuildingStorefrontIcon', label: 'Keep every booking within the company ecosystem' },
];

const clients = ['F Taxi', 'Apple Call Taxi', 'Thaani', 'Maya Call Taxi', 'FriendsTrack'];

const stats = [
  { value: '22+', label: 'Cities Active' },
  { value: '5,500+', label: 'Daily Trips' },
  { value: '14 Days', label: 'Go-Live Timeline' },
  { value: '99.9%', label: 'Platform Uptime' },
];

export default function RideHailingContent() {
  const [activeTab, setActiveTab] = useState<'driver' | 'rider'>('rider');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO ── */}
      <PageHero className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-on-scroll stagger-1">
              <Link href="/products" className="inline-flex items-center gap-2 text-white/70 text-sm font-medium mb-6 hover:text-white transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-5">
                Ride Hailing Platform
              </span>
              <h1 className="font-display text-5xl md:text-6xl font-semibold text-white tracking-tight leading-tight mb-4 mt-2">
                Take Your{' '}
                <span
                  className="italic font-semibold"
                  style={{
                    color: '#F59E0B',
                    textShadow: '0 0 32px rgba(245,158,11,0.55), 0 0 8px rgba(245,158,11,0.35)',
                  }}
                >
                  Taxi Business
                </span>{' '}
                to the Next Level
              </h1>
              <p className="text-white/70 text-lg leading-relaxed mb-8 animate-on-scroll stagger-2">
                All in One Taxi Software Platform — Built for today, Scaled for tomorrow.
              </p>
              <div className="flex flex-wrap gap-4 animate-on-scroll stagger-3">
                <Link href="/demo-booking" className="btn-primary inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl text-sm font-semibold">
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl text-sm font-medium text-white/80 border border-white/20 hover:border-white/40 hover:text-white transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>
            </div>
            <div className="animate-on-scroll stagger-2 grid grid-cols-2 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="bg-white/[0.1] border border-white/[0.2] rounded-2xl p-6 backdrop-blur-sm hover-lift transition-all duration-300">
                  <div className="font-display text-4xl font-semibold text-white mb-2">{s.value}</div>
                  <div className="text-sm text-white/85 font-medium">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </PageHero>

      {/* ── WHAT WE OFFER ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">What We Offer</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Everything your cab business{' '}
              <span className="italic font-light text-[#0459F7]">needs.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-5">
            {offerings.map((o, i) => (
              <div key={o.label} className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} flex items-center gap-4 p-5 bg-[#F5F5F5] rounded-2xl border border-[rgba(15,23,42,0.07)] hover:border-[rgba(4,89,247,0.2)] hover:shadow-md transition-all duration-300 group`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, rgba(4,89,247,0.12), rgba(10,47,216,0.06))' }}>
                  <Icon name={o.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0459F7]" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-[#0F172A]">{o.label}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{o.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── BOOKING FLOW ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Movio Taxi Booking Flow</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Multiple Booking Channels.{' '}
              <span className="italic font-light text-[#0459F7]">One Smart Platform.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            {/* Booking Channels */}
            <div className="animate-on-scroll stagger-2 bg-white rounded-2xl border border-[rgba(15,23,42,0.07)] p-6 shadow-sm">
              <h3 className="font-semibold text-[#0F172A] text-sm uppercase tracking-widest mb-5 flex items-center gap-2">
                <Icon name="UserGroupIcon" size={16} className="text-[#0459F7]" />
                Customer Booking Channels
              </h3>
              <div className="flex flex-col gap-3">
                {bookingChannels.map((c) => (
                  <div key={c.label} className="flex items-start gap-3 p-3 rounded-xl bg-[#F5F5F5] border border-[rgba(15,23,42,0.05)]">
                    <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#0459F7] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-[#0F172A]">{c.label}</p>
                      <p className="text-xs text-[#64748B] mt-0.5">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Server */}
            <div className="animate-on-scroll stagger-3 flex flex-col gap-5">
              <div className="bg-[#0F172A] rounded-2xl p-8 text-center text-white">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                  <Icon name="ServerIcon" size={30} className="text-white" />
                </div>
                <h3 className="font-display text-xl font-semibold mb-2">Taxi Server</h3>
                <p className="text-white/85 text-sm">All bookings from different channels are received in real-time</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-[rgba(4,89,247,0.2)] text-center shadow-sm">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'linear-gradient(135deg, #F59E0B20, #F59E0B10)' }}>
                  <Icon name="BoltIcon" size={22} className="text-[#F59E0B]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-1">Auto Assign</h3>
                <p className="text-sm text-[#475569]">Booking is auto assigned to nearest available vehicle</p>
              </div>
              {/* Bottom badges */}
              <div className="grid grid-cols-2 gap-2 mt-2">
                {['More Bookings', 'Real-time Tracking', 'Lower Costs', 'Cloud Based'].map((b) => (
                  <div key={b} className="text-center text-xs font-medium text-[#0459F7] bg-[rgba(4,89,247,0.08)] border border-[rgba(4,89,247,0.15)] rounded-xl py-2 px-3">{b}</div>
                ))}
              </div>
            </div>

            {/* Vehicle assignment */}
            <div className="animate-on-scroll stagger-4 bg-white rounded-2xl border border-[rgba(15,23,42,0.07)] p-6 shadow-sm">
              <h3 className="font-semibold text-[#0F172A] text-sm uppercase tracking-widest mb-5 flex items-center gap-2">
                <Icon name="TruckIcon" size={16} className="text-[#0459F7]" />
                Auto Assign to Vehicles
              </h3>
              <div className="flex flex-col gap-4">
                {vehicleAssignments.map((v) => (
                  <div key={v.label} className="flex items-center gap-4 p-4 rounded-xl border-2 transition-all" style={{ borderColor: `${v.color}30`, background: `${v.color}08` }}>
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: v.color }}>
                      <Icon name="TruckIcon" size={20} className="text-white" />
                    </div>
                    <span className="font-semibold text-[#0F172A]">{v.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 rounded-xl bg-[#0F172A] text-center">
                <p className="text-white font-semibold text-sm">Smarter Booking. Better Rides.</p>
                <p className="text-white/75 text-xs mt-1">One Platform. More Trips. Better Tomorrow.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── COMPARISON ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Comparison</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Traditional Way vs{' '}
              <span className="italic font-light text-[#0459F7]">Smarter Way with Movio Taxi</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Market Reality */}
            <div className="animate-on-scroll stagger-2 bg-[#F5F5F5] rounded-2xl p-8 border border-[rgba(15,23,42,0.07)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#FEF3C7]">
                  <Icon name="MegaphoneIcon" size={20} className="text-[#D97706]" />
                </div>
                <h3 className="font-display text-xl font-semibold text-[#0F172A]">Market Reality</h3>
              </div>
              <div className="flex flex-col gap-4">
                {marketReality.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#FEF3C7]">
                      <Icon name="ExclamationTriangleIcon" size={12} className="text-[#D97706]" />
                    </div>
                    <p className="text-sm text-[#475569] leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Movio Advantage */}
            <div className="animate-on-scroll stagger-3 rounded-2xl p-8 border border-[rgba(4,89,247,0.2)]" style={{ background: 'linear-gradient(135deg, rgba(4,89,247,0.04), rgba(10,47,216,0.02))' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                  <Icon name="TrophyIcon" size={20} className="text-white" />
                </div>
                <h3 className="font-display text-xl font-semibold text-[#0F172A]">The Movio Taxi Advantage</h3>
              </div>
              <div className="flex flex-col gap-4">
                {movioAdvantage.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                      <Icon name="CheckIcon" size={12} className="text-white" />
                    </div>
                    <p className="text-sm text-[#475569] leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-[rgba(4,89,247,0.15)]">
                <p className="font-semibold text-[#0459F7] text-sm">Smart Technology. Stronger Business.</p>
              </div>
            </div>
          </div>

          {/* Bottom trust bar */}
          <div className="animate-on-scroll stagger-4 mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
            {['Secure & Reliable', 'Live Trip Tracking', 'Driver & Customer Management', 'Business Insights', 'Cloud Based Platform'].map((item) => (
              <div key={item} className="flex items-center gap-2 p-3 bg-[#F5F5F5] rounded-xl border border-[rgba(15,23,42,0.07)]">
                <Icon name="CheckBadgeIcon" size={16} className="text-[#0459F7] flex-shrink-0" />
                <span className="text-xs font-medium text-[#475569]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FEATURES / FLEET MANAGEMENT ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Features</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Digital Fleet Management{' '}
              <span className="italic font-light text-[#0459F7]">Transformation</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">Powerful technology to streamline operations, improve efficiency and drive growth.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Fleet Benefits */}
            <div className="animate-on-scroll stagger-2 bg-white rounded-2xl p-8 border border-[rgba(15,23,42,0.07)] shadow-sm">
              <h3 className="font-display text-xl font-semibold text-[#0F172A] mb-2">We help you manage a large fleet efficiently, by ensuring:</h3>
              <div className="mt-6 flex flex-col gap-3">
                {fleetBenefits.map((b, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                      <Icon name="CheckIcon" size={12} className="text-white" />
                    </div>
                    <span className="text-sm text-[#475569]">{b}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Book → Assign → Track → Optimize */}
            <div className="animate-on-scroll stagger-3 flex flex-col gap-5">
              <div className="grid grid-cols-4 gap-3">
                {[
                  { icon: 'DevicePhoneMobileIcon', label: 'Book' },
                  { icon: 'UserIcon', label: 'Assign' },
                  { icon: 'MapPinIcon', label: 'Track' },
                  { icon: 'ChartBarIcon', label: 'Optimize' },
                ].map((step, i) => (
                  <div key={step.label} className="relative flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-2" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                      <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-white" />
                    </div>
                    <span className="text-xs font-semibold text-[#0F172A]">{step.label}</span>
                    {i < 3 && <div className="absolute top-7 left-full w-3 h-0.5 bg-[#0459F7] hidden lg:block" />}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'EyeIcon', title: 'Real-time Visibility', desc: 'Track vehicles, drivers and trips in real time' },
                  { icon: 'AdjustmentsHorizontalIcon', title: 'Better Control', desc: 'Centralized operations and policy enforcement' },
                  { icon: 'BoltIcon', title: 'Maximize Efficiency', desc: 'Optimize routes, reduce downtime and save cost' },
                  { icon: 'ChartBarIcon', title: 'Grow Your Business', desc: 'Data-driven insights to scale and succeed' },
                ].map((c) => (
                  <div key={c.title} className="bg-white rounded-xl p-5 border border-[rgba(15,23,42,0.07)]">
                    <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#0459F7] mb-3" />
                    <p className="font-semibold text-sm text-[#0F172A] mb-1">{c.title}</p>
                    <p className="text-xs text-[#475569]">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Fleet Management Features Grid */}
          <div className="animate-on-scroll stagger-4">
            <h3 className="font-display text-2xl font-semibold text-[#0F172A] mb-6 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                <Icon name="ComputerDesktopIcon" size={18} className="text-white" />
              </div>
              Fleet Management
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {fleetFeatures.map((f, i) => (
                <div key={f.label} className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} bg-white rounded-xl p-5 border border-[rgba(15,23,42,0.07)] hover:border-[rgba(4,89,247,0.2)] hover:shadow-md transition-all duration-300 group`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, rgba(4,89,247,0.12), rgba(10,47,216,0.06))' }}>
                    <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#0459F7]" />
                  </div>
                  <p className="font-semibold text-sm text-[#0F172A] mb-1">{f.label}</p>
                  <p className="text-xs text-[#64748B]">{f.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── DRIVER APP & RIDER APP ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-6">
            <span className="section-label block mb-3">App Features</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Seamless experience for both{' '}
              <span className="italic font-light text-[#0459F7]">Customers and Drivers.</span>
            </h2>
          </div>

          {/* Badges */}
          <div className="animate-on-scroll stagger-2 flex flex-wrap justify-center gap-4 mb-12">
            {['Better Experience', 'Improved Efficiency', 'Safe & Reliable', 'Business Growth'].map((b) => (
              <div key={b} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[rgba(4,89,247,0.2)] bg-[rgba(4,89,247,0.05)]">
                <Icon name="StarIcon" size={14} className="text-[#0459F7]" />
                <span className="text-sm font-semibold text-[#0459F7]">{b}</span>
              </div>
            ))}
          </div>

          {/* Tab toggle */}
          <div className="animate-on-scroll stagger-3 flex justify-center mb-8">
            <div className="flex gap-2 p-1 bg-[#F5F5F5] rounded-2xl border border-[rgba(15,23,42,0.07)]">
              <button
                onClick={() => setActiveTab('rider')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${activeTab === 'rider' ? 'bg-white shadow-sm text-[#0F172A] border border-[rgba(15,23,42,0.08)]' : 'text-[#475569] hover:text-[#0F172A]'}`}
              >
                <Icon name="UserIcon" size={16} className={activeTab === 'rider' ? 'text-[#0459F7]' : ''} />
                Rider App
              </button>
              <button
                onClick={() => setActiveTab('driver')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${activeTab === 'driver' ? 'bg-white shadow-sm text-[#0F172A] border border-[rgba(15,23,42,0.08)]' : 'text-[#475569] hover:text-[#0F172A]'}`}
              >
                <Icon name="TruckIcon" size={16} className={activeTab === 'driver' ? 'text-[#0A2FD8]' : ''} />
                Driver App
              </button>
            </div>
          </div>

          {/* Rider App panel */}
          <div className={`bg-[#F5F5F5] rounded-3xl border border-[rgba(15,23,42,0.07)] p-8 md:p-10 transition-all duration-300 ${activeTab === 'rider' ? 'block' : 'hidden'}`}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                <Icon name="UserIcon" size={26} className="text-white" />
              </div>
              <div>
                <h3 className="font-display text-2xl font-semibold text-[#0F172A]">Rider App</h3>
                <p className="text-sm text-[#475569]">iOS & Android</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {riderAppFeatures.map((f) => (
                <div key={f} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-[rgba(15,23,42,0.06)]">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                    <Icon name="CheckIcon" size={12} className="text-white" />
                  </div>
                  <span className="text-sm font-medium text-[#0F172A]">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Driver App panel */}
          <div className={`bg-[#F5F5F5] rounded-3xl border border-[rgba(15,23,42,0.07)] p-8 md:p-10 transition-all duration-300 ${activeTab === 'driver' ? 'block' : 'hidden'}`}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0A2FD8, #0321A8)' }}>
                <Icon name="TruckIcon" size={26} className="text-white" />
              </div>
              <div>
                <h3 className="font-display text-2xl font-semibold text-[#0F172A]">Driver App</h3>
                <p className="text-sm text-[#475569]">iOS & Android</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {driverAppFeatures.map((f) => (
                <div key={f} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-[rgba(15,23,42,0.06)]">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0A2FD8, #0321A8)' }}>
                    <Icon name="CheckIcon" size={12} className="text-white" />
                  </div>
                  <span className="text-sm font-medium text-[#0F172A]">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── KIOSK BOOKING ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-on-scroll stagger-1">
              <span className="section-label block mb-3">Kiosk Booking</span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
                Book a Taxi with <span className="italic font-light text-[#0459F7]">Kiosk</span> in just One Click
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">
                A smart and intuitive self-service solution for modern transportation. An automated self-service terminal that allows users to book a taxi or ride-sharing service.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {kioskFeatures.map((f) => (
                  <div key={f.label} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-[rgba(15,23,42,0.07)]">
                    <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#0459F7]" />
                    <span className="text-sm font-semibold text-[#0F172A]">{f.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="animate-on-scroll stagger-2 flex flex-col gap-6">
              {/* How It Works */}
              <div className="bg-white rounded-2xl p-7 border border-[rgba(15,23,42,0.07)] shadow-sm">
                <h3 className="font-semibold text-sm uppercase tracking-widest text-[#64748B] mb-5">How It Works</h3>
                <div className="flex flex-col gap-3">
                  {kioskSteps.map((step, i) => (
                    <div key={step.label} className="flex items-center gap-4">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-white text-sm" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                        {i + 1}
                      </div>
                      <span className="text-sm font-medium text-[#0F172A]">{step.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Benefits */}
              <div className="grid grid-cols-2 gap-4">
                {kioskBenefits.map((b) => (
                  <div key={b.label} className="bg-white rounded-xl p-5 border border-[rgba(15,23,42,0.07)] hover:border-[rgba(4,89,247,0.2)] transition-all">
                    <Icon name={b.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#0459F7] mb-3" />
                    <p className="font-semibold text-sm text-[#0F172A] mb-1">{b.label}</p>
                    <p className="text-xs text-[#64748B]">{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── WHATSAPP BOOKING ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-on-scroll stagger-1 order-2 lg:order-1 flex flex-col gap-5">
              <div className="bg-[#F0FDF4] rounded-2xl p-7 border border-[rgba(37,211,102,0.2)]">
                <h3 className="font-semibold text-sm uppercase tracking-widest text-[#16A34A] mb-5">Why WhatsApp Taxi Booking?</h3>
                <div className="flex flex-wrap gap-3">
                  {whatsappBenefits.map((b) => (
                    <div key={b.label} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-[rgba(37,211,102,0.15)]">
                      <Icon name={b.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-[#16A34A]" />
                      <span className="text-xs font-semibold text-[#0F172A]">{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#F5F5F5] rounded-2xl p-7 border border-[rgba(15,23,42,0.07)]">
                <h3 className="font-semibold text-sm uppercase tracking-widest text-[#64748B] mb-5">How It Works</h3>
                <div className="flex flex-col gap-3">
                  {whatsappSteps.map((s) => (
                    <div key={s.step} className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
                        {s.step}
                      </div>
                      <span className="text-sm font-medium text-[#0F172A]">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="animate-on-scroll stagger-2 order-1 lg:order-2">
              <span className="section-label block mb-3">WhatsApp Booking</span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
                Generate Taxi Booking Using{' '}
                <span className="italic font-light text-[#25D366]">WhatsApp Chatbots</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-4">
                Cab booking through WhatsApp is an increasingly popular and convenient way to book a taxi using a messaging app, leveraging the widespread use of WhatsApp.
              </p>
              <p className="text-[#0F172A] font-semibold text-base">
                Just by sharing — <span className="text-[#25D366]">Current Location, Drop Location, Time and Car Type</span> to Book a Taxi in Minutes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── CORPORATE ACCOUNT ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="section-label block mb-3">Corporate Account</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
              Designed for Businesses.{' '}
              <span className="italic font-light text-[#0459F7]">Built for Efficiency.</span>
            </h2>
            <p className="text-[#475569] text-lg max-w-2xl mx-auto">A smarter way for businesses to manage taxi bookings, streamline operations and control expenses.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {corporateFeatures.map((f, i) => (
              <div key={f.label} className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} bg-white rounded-2xl p-7 border border-[rgba(15,23,42,0.07)] hover:border-[rgba(4,89,247,0.2)] hover:shadow-md transition-all duration-300 group`}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, rgba(4,89,247,0.12), rgba(10,47,216,0.06))' }}>
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#0459F7]" />
                </div>
                <h3 className="font-display text-base font-semibold text-[#0F172A] mb-2">{f.label}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          <div className="animate-on-scroll stagger-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {['Increase Productivity', 'Enhanced Control', 'Cost Savings', 'Better Travel Experience'].map((b) => (
              <div key={b} className="bg-white rounded-xl p-4 border border-[rgba(15,23,42,0.07)] text-center">
                <Icon name="CheckBadgeIcon" size={22} className="text-[#0459F7] mx-auto mb-2" />
                <p className="text-sm font-semibold text-[#0F172A]">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── DRIVER BOOKING ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-on-scroll stagger-1">
              <span className="section-label block mb-3">Driver Booking</span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
                Turn Personal Calls Into{' '}
                <span className="italic font-light text-[#0459F7]">More Earnings</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-8">
                When regular customers call directly, drivers can instantly create bookings through the driver app and keep every trip connected to the company platform for safe and seamless travel.
              </p>
              <div className="bg-[#0F172A] rounded-2xl p-6 text-white">
                <p className="font-display text-xl font-semibold italic leading-relaxed text-[#1B8DFF]">
                  "No WhatsApp Confusion. No Lost Customers. Safe Bookings. More Earnings."
                </p>
              </div>
            </div>
            <div className="animate-on-scroll stagger-2">
              <h3 className="font-semibold text-sm uppercase tracking-widest text-[#64748B] mb-5">Why Choose Driver Booking Over WhatsApp Groups?</h3>
              <div className="grid gap-3">
                {driverBookingBenefits.map((b) => (
                  <div key={b.label} className="flex items-center gap-3 p-4 bg-[#F5F5F5] rounded-xl border border-[rgba(15,23,42,0.07)]">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(4,89,247,0.12), rgba(10,47,216,0.06))' }}>
                      <Icon name={b.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-[#0459F7]" />
                    </div>
                    <span className="text-sm font-medium text-[#0F172A]">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CITIES & CLIENTS ── */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-on-scroll stagger-1">
              <span className="section-label block mb-3">Our Reach</span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
                Smart Technology.{' '}
                <span className="italic font-light text-[#0459F7]">Smoother Rides.</span>
              </h2>
              <div className="flex items-center gap-4 mb-5">
                <div className="bg-[#0F172A] text-white font-display text-4xl font-semibold px-5 py-3 rounded-2xl">22</div>
                <div>
                  <p className="font-semibold text-[#0F172A]">Cities Active</p>
                  <p className="text-sm text-[#475569]">Across Tamil Nadu</p>
                </div>
              </div>
              <p className="text-[#475569] text-base leading-relaxed mb-6">
                Our taxi platform is actively operating in 22 cities across Tamil Nadu and expanding soon to{' '}
                <span className="font-semibold text-[#0459F7]">Kerala, Karnataka, and major North Indian cities.</span>
              </p>
              <div className="flex flex-wrap gap-2">
                {['Tamil Nadu', 'Kerala', 'Karnataka', 'North India'].map((state, i) => (
                  <span key={state} className={`px-4 py-2 rounded-full text-sm font-semibold border ${i === 0 ? 'bg-[#0459F7] text-white border-[#0459F7]' : 'bg-white text-[#0459F7] border-[rgba(4,89,247,0.3)]'}`}>
                    {i > 0 ? '↗ ' : ''}{state}
                  </span>
                ))}
              </div>
            </div>
            <div className="animate-on-scroll stagger-2">
              <h3 className="font-semibold text-sm uppercase tracking-widest text-[#64748B] mb-6">Our Esteemed Clients in Tamil Nadu</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {clients.map((c) => (
                  <div key={c} className="bg-white rounded-2xl p-5 border border-[rgba(15,23,42,0.07)] flex items-center justify-center text-center hover:border-[rgba(4,89,247,0.2)] hover:shadow-md transition-all duration-300">
                    <div>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: 'linear-gradient(135deg, #0459F7, #0A2FD8)' }}>
                        <Icon name="TruckIcon" size={20} className="text-white" />
                      </div>
                      <p className="font-semibold text-sm text-[#0F172A]">{c}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0A2FD8 0%, #0459F7 50%, #1B8DFF 100%)' }}>
            {/* subtle grid overlay */}
            <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Ready to Launch?
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              Your branded cab app,{' '}
              <span className="italic font-light text-white/85">live in 14 days.</span>
            </h2>

            <p className="relative text-white/75 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/80 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={15} className="text-white/80" />
                +91 9600830488
              </a>
              <span className="text-white/40 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/80 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={15} className="text-white/80" />
                contact@movio.in
              </a>
              <span className="text-white/40 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2 text-white/80 font-medium">
                <Icon name="GlobeAltIcon" size={15} className="text-white/80" />
                www.movio.in
              </span>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold bg-white text-[#0459F7] hover:bg-white/90 shadow-lg transition-all duration-200"
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/40 hover:border-white/80 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
